import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface DashboardFilters {
  search: string;
  statuses: string[];
  labels: string[];
  perPage: number;
  sortIndex: number | null;
  sortDirection: 'asc' | 'desc';
  showAllStatuses: boolean;
}

interface UseDashboardFiltersUrlReturn {
  initialFilters: Partial<DashboardFilters>;
  updateUrl: (filters: DashboardFilters) => void;
}

export const useDashboardFiltersUrl = (): UseDashboardFiltersUrlReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialFilters = useMemo((): Partial<DashboardFilters> => {
    const params = new URLSearchParams(location.search);

    const filters: Partial<DashboardFilters> = {};

    // Search
    const search = params.get('search');
    if (search) {
      filters.search = search;
    }

    // Statuses
    const statuses = params.get('statuses');
    if (statuses) {
      filters.statuses = statuses.split(',').filter(Boolean);
    }

    // Per page
    const perPage = params.get('perPage');
    if (perPage) {
      const perPageNum = parseInt(perPage, 10);
      if (!isNaN(perPageNum) && [10, 20, 50].includes(perPageNum)) {
        filters.perPage = perPageNum;
      }
    }

    // Sort index
    const sortIndex = params.get('sortIndex');
    if (sortIndex) {
      const sortIndexNum = parseInt(sortIndex, 10);
      if (!isNaN(sortIndexNum) && sortIndexNum >= 0) {
        filters.sortIndex = sortIndexNum;
      }
    }

    // Sort direction
    const sortDirection = params.get('sortDir');
    if (sortDirection === 'asc' || sortDirection === 'desc') {
      filters.sortDirection = sortDirection;
    }

    // Labels
    const labels = params.get('labels');
    if (labels) {
      filters.labels = labels.split(',').filter(Boolean);
    }

    // Show all statuses
    const showAllStatuses = params.get('allStatuses');
    if (showAllStatuses === 'true') {
      filters.showAllStatuses = true;
    }

    return filters;
  }, [location.search]);

  const updateUrl = useCallback(
    (filters: DashboardFilters) => {
      const params = new URLSearchParams();

      // Search - only add if not empty
      if (filters.search) {
        params.set('search', filters.search);
      }

      // Statuses - only add if not empty
      if (filters.statuses && filters.statuses.length > 0) {
        params.set('statuses', filters.statuses.join(','));
      }

      // Per page - only add if not default (20)
      if (filters.perPage && filters.perPage !== 20) {
        params.set('perPage', String(filters.perPage));
      }

      // Sort - only add if sorting is active
      if (filters.sortIndex !== null) {
        params.set('sortIndex', String(filters.sortIndex));
        params.set('sortDir', filters.sortDirection);
      }

      // Labels - only add if not empty
      if (filters.labels && filters.labels.length > 0) {
        params.set('labels', filters.labels.join(','));
      }

      // Show all statuses - only add if true
      if (filters.showAllStatuses) {
        params.set('allStatuses', 'true');
      }

      const search = params.toString();
      const newUrl = search ? `?${search}` : location.pathname;

      // Only update if URL actually changed
      if (location.search !== (search ? `?${search}` : '')) {
        navigate(newUrl, { replace: true });
      }
    },
    [location.pathname, location.search, navigate]
  );

  return { initialFilters, updateUrl };
};
